import { useEffect, useRef, useState } from 'react';
import { Send, Mail, MessageSquare, Smartphone } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { relative } from '@/lib/format';
import {
  Button, Card, EmptyState, LoadingBlock, Textarea, cn,
} from '@/components/ui';
import { usePortal } from './PortalShell';
import type { Message } from '@shared/types';

/**
 * The tenant's side of the conversation.
 *
 * Everything in one thread, whichever way it travelled: texts and emails the
 * landlord sent, and anything the tenant has written back. A tenant should not
 * have to remember which channel something arrived on in order to find it
 * again — and having their own messages on the same timeline is what makes it
 * obvious that somebody has, or has not, replied.
 */
export default function PortalMessages() {
  const { data: portal, reload: reloadPortal } = usePortal();
  const { data, loading, reload } = useApi<Message[]>('/portal/messages');

  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  /*
   * Reading the thread marks it read on the server, so the unread badge in the
   * header is wrong the moment this page loads. Refreshed once, after the
   * first fetch settles — a ref rather than a dependency, because refreshing
   * on every `loading` transition is refreshing on the transition the refresh
   * itself causes.
   */
  const badgeRefreshed = useRef(false);
  useEffect(() => {
    if (loading || badgeRefreshed.current) return;
    badgeRefreshed.current = true;
    reloadPortal();
  }, [loading, reloadPortal]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [data?.length]);

  const send = async () => {
    const text = body.trim();
    if (text.length === 0) return;
    setSending(true);
    setError(null);
    try {
      await api.post('/portal/messages', { body: text });
      setBody('');
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const messages = (data ?? []).slice().reverse();

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-extrabold tracking-tight">Messages</h1>
        <p className="text-sm text-muted mt-1">
          Anything {portal.org.name} has sent you, and anything you send them.
          {portal.org.contact_phone
            ? ` For something urgent, call ${portal.org.contact_phone}.`
            : ''}
        </p>
      </div>

      <Card className="p-4 space-y-3">
        <Textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={`Write to ${portal.org.name}…`}
          maxLength={2000}
        />
        <div className="flex items-center gap-3">
          <p className="text-xs text-faint flex-1">
            They are emailed as soon as you send this. Replies appear here.
          </p>
          <Button
            variant="primary"
            onClick={send}
            loading={sending}
            disabled={body.trim().length === 0}
            icon={<Send className="h-4 w-4" />}
          >
            Send
          </Button>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
      </Card>

      {loading && !data ? (
        <LoadingBlock rows={4} />
      ) : messages.length === 0 ? (
        <EmptyState
          icon={<MessageSquare className="h-6 w-6" />}
          title="Nothing yet"
          message={`Nothing has been sent to you, and you have not written to ${portal.org.name}. Anything either of you sends will appear here.`}
        />
      ) : (
        <div className="space-y-3">
          {messages.map((message) => {
            const mine = message.direction === 'inbound';
            return (
              <div
                key={message.id}
                className={cn('flex', mine ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-3 space-y-1',
                    mine
                      ? 'bg-brand/12 border border-brand/25'
                      : 'bg-surface border border-line',
                  )}
                >
                  {message.subject && (
                    <p className="text-sm font-semibold">{message.subject}</p>
                  )}
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.body}</p>
                  <p className="text-[11px] text-faint flex items-center gap-1.5 pt-0.5">
                    <ChannelIcon channel={message.channel} />
                    {mine ? 'You' : portal.org.name}
                    {' · '}
                    {relative(message.created_at)}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
      )}
    </div>
  );
}

/** Says how a message reached them, so "I never got the text" is checkable. */
function ChannelIcon({ channel }: { channel?: string }) {
  if (channel === 'email') return <Mail className="h-3 w-3" aria-label="Sent by email" />;
  if (channel === 'sms') return <Smartphone className="h-3 w-3" aria-label="Sent by text" />;
  return <MessageSquare className="h-3 w-3" aria-label="Sent in this account" />;
}
